/**
 * Abrir um tópico.
 *
 * Um só componente para os dois sítios de onde se abre — o "+" na lista de
 * tópicos da sidebar e o botão numa mensagem — porque a diferença entre eles é
 * um campo: o `messageId`. Dois modais quase iguais era a maneira certa de o
 * limite de nome ficar validado num e não no outro.
 *
 * Não há aqui escolha de categoria nem de tipo, ao contrário do `ChannelCreate`:
 * um tópico nasce sempre dentro do canal de onde é aberto, e é isso que lhe dá
 * as permissões. Deixar mudar o pai era deixar contornar um canal privado.
 */

import { Limits } from "@greenvoice/shared";
import { useState } from "react";

import { useStore } from "../store/store";
import { nameError } from "./ChannelSettings";
import { IconClose } from "./Icons";

export function ThreadCreate({
  channelId,
  messageId,
  onClose,
}: {
  channelId: string;
  /** Quando vem, o tópico nasce daquela mensagem — e só pode nascer uma vez. */
  messageId?: string | undefined;
  onClose: () => void;
}) {
  const createThread = useStore((s) => s.createThread);
  const selectChannel = useStore((s) => s.selectChannel);
  // O dicionário inteiro (referência estável) e derivar no render: um selector
  // que devolvesse um objecto novo a cada chamada dispara o loop do zustand v5.
  const messages = useStore((s) => s.messages);
  const source = messageId ? messages[channelId]?.find((m) => m.id === messageId) : undefined;

  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalid = nameError(name);

  async function create() {
    if (invalid) {
      setError(invalid);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const thread = await createThread(channelId, name.trim(), messageId ?? null);
      // Entra logo no tópico: quem o abre é para lá escrever. Deixá-lo no canal
      // de onde saiu obrigava a ir procurá-lo na sidebar a seguir.
      selectChannel(thread.id);
      onClose();
    } catch (caught) {
      // Fica no modal com o nome escrito: a falha mais provável aqui é o 409 de
      // "essa mensagem já tem um tópico", e nesse caso fechar a janela sem dizer
      // nada era o pior desfecho possível.
      setError((caught as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Abrir tópico">
      <div className="modal__panel">
        <header className="modal__head">
          <h2>Abrir tópico</h2>
          <button type="button" className="icon-button" onClick={onClose} title="Fechar">
            <IconClose size={18} />
          </button>
        </header>

        <form
          className="settings__form"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          {source ? (
            // O texto da mensagem de origem, cortado. Quem carrega no botão de
            // uma mensagem numa lista comprida já rolou para outro sítio quando
            // o modal abre — sem isto não sabe de qual delas se trata.
            <p className="thread-create__source" title={source.content}>
              {source.content.slice(0, 140) || "(mensagem sem texto)"}
            </p>
          ) : null}

          <label className="field">
            <span>Nome</span>
            <input
              value={name}
              autoFocus
              maxLength={Limits.channelName.max}
              placeholder="sobre-o-orçamento"
              aria-invalid={name.length > 0 && invalid !== null}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <div className="settings__foot">
            <button type="submit" className="button button--primary" disabled={busy || invalid !== null}>
              Abrir
            </button>
          </div>

          {error ? (
            <p className="auth__error" role="alert">
              {error}
            </p>
          ) : null}
        </form>
      </div>
    </div>
  );
}
